import {
  FetchApi,
  fetchApiRef,
  useApi,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import {
  Component,
  DomainType,
  DomainStructure,
  Resource,
  System,
} from '../pages/request/types';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  ScaffolderApi,
  scaffolderApiRef,
} from '@backstage/plugin-scaffolder-react';
import { ComponentEntity, parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { DiscoveryApi } from '@backstage/core-plugin-api';
import { resourceTypeMapper } from './resourceTemplates';
import { SelectItem } from '@backstage/core-components';
import { ResourceType } from '../pages/request/types';
import {
  convertToEntityType,
  ListRequestItem,
  RequestStatus,
  RequestType,
} from '@internal/backstage-plugin-request-manager-common';

export const componentName = (component: ComponentEntity): string => {
  return `${component.metadata.name}-${component.spec?.type || 'default'}`;
}

export const componentFolder = (component: ComponentEntity): string => {
  return component.metadata.name.replaceAll('-','_');
}

const extractNamespace = (entityRefString: string): string => {
  const entity = parseEntityRef(entityRefString);
  return entity.kind === 'domain' ? entity.name : entity.namespace;
};

export class RequestService {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly catalogApi: CatalogApi,
    private readonly discoveryApi: DiscoveryApi,
    private readonly scaffolderApi: ScaffolderApi,
  ) {}

  private async getApiUrl(op: string = ''): Promise<string> {
    const apiUrl = `${await this.discoveryApi.getBaseUrl(
      'request-manager',
    )}/requests${op}`;
    return apiUrl;
  }

  public async listRequests(
    selectedStatus?: RequestStatus,
    selectedTypes?: RequestType,
    selectedSystem?: string
  ): Promise<ListRequestItem[]> {
    const url = new URL(await this.getApiUrl());
    if (selectedStatus) url.searchParams.append('status', selectedStatus);
    if (selectedTypes) url.searchParams.append('type', selectedTypes);
    if (selectedSystem) url.searchParams.append('system', selectedSystem);

    const requests = await this.fetchApi.fetch(url).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch requests: ${response.statusText}`);
      }
      return response.json() as Promise<ListRequestItem[]>;
    });

    return requests;
  }

  public async existsByNameAndOwner(name: string, owner: string, type: RequestType): Promise<boolean> {
    if (!name || name.trim() === '' || !owner || owner.trim() === '') {
      return false;
    }

    try {
      const response = await this.catalogApi.getEntityByRef({
        kind: convertToEntityType(type),
        name: name,
        namespace: extractNamespace(owner),
      });
      return !!response;
    } catch (error) {
      return false;
    }
  }

  public async cancelRequest(id: string | undefined): Promise<void> {
    if (id === undefined) return;
    const url = new URL(await this.getApiUrl(`/cancel/${id}`));

    await this.fetchApi.fetch(url, { method: 'PUT' }).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to cancel Request: ${response.statusText}`);
      }
    });
  }

  public async startRequest(id: string | undefined): Promise<void> {
    if (id === undefined) return;
    const url = new URL(await this.getApiUrl(`/start/${id}`));

    await this.fetchApi.fetch(url, { method: 'PUT' }).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to start Request: ${response.statusText}`);
      }
    });
  }

  public async finishRequest(id: string | undefined): Promise<void> {
    if (id === undefined) return;
    const url = new URL(await this.getApiUrl(`/finish/${id}`));

    await this.fetchApi.fetch(url, { method: 'PUT' }).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to finish Request: ${response.statusText}`);
      }
    });
  }

  public async listSystems(): Promise<SelectItem[]> {
    const response = await this.catalogApi.getEntities({
      filter: { kind: 'System' },
      fields: ['kind', 'metadata.title', 'metadata.name', 'metadata.namespace'],
    });

    return response.items.map(item => ({
      value: stringifyEntityRef(item),
      label: item.metadata.title || item.metadata.name,
    }));
  }

  public async listDomains(type: DomainType): Promise<SelectItem[]> {
    const response = await this.catalogApi.getEntities({
      filter: { kind: 'Domain', 'spec.type': type },
      fields: ['kind', 'metadata.title', 'metadata.name', 'metadata.namespace'],
    });

    return response.items.map(item => ({
      value: stringifyEntityRef(item),
      label: item.metadata.title || item.metadata.name,
    }));
  }

  public async listUsers(): Promise<SelectItem[]> {
    const response = await this.catalogApi.getEntities({
      filter: { kind: 'User' },
      fields: [
        'kind',
        'spec.profile.displayName',
        'metadata.name',
        'metadata.namespace',
      ],
    });

    return response.items.map(item => ({
      value: stringifyEntityRef(item),
      label: (item as any).spec?.profile?.displayName || item.metadata.name,
    }));
  }

  /**
   * Get the Azure DevOps team project associated with a system domain.
   * If the system domain has an annotation for the team project, it will return that.
   * If not, it will look for the team project in the parent domain.
   */
  private async getTeamProject(systemDomainName: string): Promise<string> {
    const systemDomain = await this.catalogApi.getEntityByRef(systemDomainName.startsWith('domain:') ? systemDomainName : `domain:${systemDomainName}`);

    if (!systemDomain) return '';

    const teamProject = systemDomain.metadata.annotations?.['dev.azure.com/team-project'];
    if (teamProject) return teamProject;

    const systemDomainDomainName = systemDomain.spec!.type === 'bundle'
      ? `domain:${systemDomain.metadata.namespace}/${systemDomain.metadata.name}`
      : `domain:${systemDomain.spec?.subdomainOf}`;

    const systemDomainDomain = await this.catalogApi.getEntityByRef(systemDomainDomainName.toString());

    return systemDomainDomain?.metadata.annotations?.['dev.azure.com/team-project'] || '';
  }

  public async createComponent(component: Component): Promise<void> {
    const entity = await this.catalogApi.getEntityByRef(component.system);
    const owner = `group:${entity?.spec?.owner}`;
    const systemDomainName = `${entity?.spec?.domain}`;
    const teamProject = await this.getTeamProject(systemDomainName);

    const response = await this.scaffolderApi.scaffold({
      templateRef: 'default/product-component-management',
      values: {
        ...component,
        owner,
        teamProject,
      },
    });
		window.location.href = `/create/tasks/${response.taskId}`;
  }

  public async createDomainStructure(
    domainStructure: DomainStructure,
  ): Promise<void> {
    const subdomainFieldMap: Record<string, string> = {
      'product-area': 'subDomainOfBundle',
      'product-group': 'subDomainOfProductArea',
    };

    const subdomainField = subdomainFieldMap[domainStructure.domainType];

    let values: any = {
      ...domainStructure,
      isNewDomain: true,
      subdomainOf: undefined,
    };

    if (subdomainField) {
      values = {
        ...values,
        [subdomainField]: domainStructure.subdomainOf,
      };
    }

    const response = await this.scaffolderApi.scaffold({
      templateRef: 'default/domain-create',
      values: values,
    });
		window.location.href = `/create/tasks/${response.taskId}`;
  }

  public async createResource(resource: Resource): Promise<void> {
    const config =
      resourceTypeMapper[resource.type as keyof typeof ResourceType];
    if (!config) {
      throw new Error(
        `No scaffolder template mapping found for resource type "${resource.type}"`,
      );
    }

    const { templateRef, getValues } = config;

    const response = await this.scaffolderApi.scaffold({
      templateRef,
      values: getValues(resource),
    });
		window.location.href = `/create/tasks/${response.taskId}`;
  }

  public async createSystem(system: System): Promise<void> {

    const valuesData = {
      owner: system.owner,
      domain: system.domain,
      subdomain: system.subdomain,
      namespace: system.namespace,
      name: system.name,
      title: system.title,
      description: system.description,
      tags: system.tags,
      appCode: system.identityAppCode,
      rac: system.racId,
      smartTalk: system.smartTalkId,
      tpd: system.tpdId,
      releaseProcess: system.releaseProcess,
      useDatabase: system.useDatabase,
      migrationProcess: system.migrationProcess,
      useConfigServer: system.useConfigServer,
      deploymentProcess: 'RollingUpdate',
      segmentDirector: system.segmentOrBoard,
      onboardingType: system.onboardingType,
      marketing: system.marketing,
      dataPrivacy: system.dataPrivacy,
      stakeHolder: system.stakeholder,
      productResponsible: system.productOwner,
      technicalResponsible: system.technicalResponsible,
      operationalResponsible: system.operationsResponsible,
      securityResponsible: system.securityResponsible,
    };

    const response = await this.scaffolderApi.scaffold({
      templateRef: 'default/product-management',
      values: valuesData,
    });

		window.location.href = `/create/tasks/${response.taskId}`;
  }

  public async completeProduct(req: ListRequestItem, args: any): Promise<void> {
    const response = await this.catalogApi.getEntities({
      filter: { kind: 'Component', ['relations.partof']: [req.system] }
    });

    const components = response.items as ComponentEntity[];
    const entity = await this.catalogApi.getEntityByRef(req.system);
    const entityAnnotations = entity?.metadata.annotations || {};
    const namespace = entity?.metadata.namespace || '';
    const gcpProjectName = args.gcp_prd;
    const useConfigServer = entityAnnotations['open.totvs.io/use-configserver'] === 'yes';

    // Prepare components data with minimal required fields
    const componentsData = components.map((c) => {
      const cname = componentName(c);
      const mname = c.metadata.name;
      const cannotations = c.metadata?.annotations || {};
      const ingresspath = cannotations['backstage.io/kubernetes-ingress-path'];

      return {
        component: mname,
        folder: componentFolder(c),
        type: c.spec.type,
        image: `us-central1-docker.pkg.dev/${gcpProjectName}/totvsapps-${namespace}/${mname}`,
        hostPrefix: '*.logistica',
        ingressPath: ingresspath || `/${cname}/${c.spec.type}`
      };
    });

    // Prepare open configuration components if needed
    const openConfigurationComponents = useConfigServer
      ? components
          .filter(c => c.spec.type === 'service')
          .map((c) => ({
            name: c.metadata.name,
            deploymentName: componentName(c),
          }))
      : [];

    const valuesData = {
      system: req.system,
      projectRepoName: args.repo,
      gcp_prd: args.gcp_prd,
      gcp_stg: args.gcp_stg,
      components: componentsData,
      openConfigurationComponents,
      requestId: req.id,
    };

    const taskresponse = await this.scaffolderApi.scaffold({
      templateRef: 'default/systemcd-management',
      values: valuesData,
    });

    window.location.href = `/create/tasks/${taskresponse.taskId}`;
  }

  public async completeTotvsResource(req: ListRequestItem, args: any):Promise<void> {

    let appCode = null;
    let rac = null;
    let smartTalk = null;
    let tpd = null;

    switch (req.assetType) {
      case 'totvs-identity':
        appCode= {id:args};
        break;

      case 'totvs-rac':
        rac= {id:args};
        break;

      case 'totvs-smarttalk':
        smartTalk= {id:args};
        break;

      case 'totvs-tpd':
        tpd= {id:args};
        break;

      default:
        return;
    }

    const values = {
      system: req.system,
      appCode: appCode,
      rac: rac,
      smartTalk: smartTalk,
      tpd: tpd
    }

    const response = await this.scaffolderApi.scaffold({
      templateRef: 'default/resource-support-element',
      values: values,
    });
		window.location.href = `/create/tasks/${response.taskId}`;
  }

}

export const useRequestService = (): RequestService => {
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);

  return new RequestService(fetchApi, catalogApi, discoveryApi, scaffolderApi);
};
